<template>
  <div>
    <RightDrawer v-if="!mobile && !loading" width="300"></RightDrawer>
    <v-container
      fluid
      :class="{
        'mx-12': !mobile,
        'my-6': !mobile,
        'my-3': mobile,
        'd-flex': true,
        'flex-column': true,
        'justify-start': true,
        'main-container': true
      }"
    >
      <div v-if="!mobile" class="d-flex flex-row align-center mb-4">
        <div class="card-label d-flex flex-row mb-4">
          <v-icon>mdi-office-building</v-icon>
          <p id="title" class="mb-0 ml-4 subtitle-1 font-weight-bold">Units</p>
        </div>
        <div class="flex-grow-1"></div>
        <div v-show="arrangeTreeMenu">
          <v-tooltip v-model="arrangeTreeMenu" bottom>
            <template v-slot:activator="{ attrs }">
              <v-btn
                id="tree-menu-deactivate-arrange"
                class="mx-2"
                rounded
                depressed
                small
                color="primary"
                v-bind="attrs"
                @click="deactivateArrangeTreeMenu"
                >Close editor</v-btn
              >
            </template>
            <span style="color: white"
              >Close the editor here to avoid accidentally moving things</span
            >
          </v-tooltip>
        </div>
        <v-menu left offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" icon v-on="on">
              <v-icon>mdi-dots-horizontal</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item
              v-for="(item, index) in menuItems"
              :key="`menuItem_${index}`"
              :disabled="item.disabled"
              @click="item.action"
            >
              <v-list-item-icon class="mr-2">
                <v-icon small>{{ item.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <div v-else class="d-flex flex-column justify-center mb-4">
        <div class="d-flex flex-row align-center">
          <v-icon>mdi-office-building</v-icon>
          <span id="title" class="ml-2 subtitle-1 font-weight-bold">Units</span>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            rounded
            depressed
            :disabled="!access.create"
            x-small
            @click="newGroup(property.rootGroupId)"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </div>
      </div>
      <div>
        <v-row>
          <v-col class="py-0" cols="12" sm="6" md="6">
            <v-text-field
              v-model="searchString"
              solo
              flat
              append-icon="mdi-magnify"
              label="Search Unit / Tenant"
              @keydown.enter="fuzzySearch"
              @click:append="fuzzySearch"
            >
            </v-text-field>
          </v-col>
          <v-col class="py-0" cols="12" sm="3" md="3">
            <v-select
              v-model="filterStatus"
              solo
              flat
              label="Show Units"
              :items="filterItems"
              @change="fuzzySearch"
            >
            </v-select>
          </v-col>
          <v-col class="py-0" cols="12" sm="3" md="3">
            <v-select
              v-model="filterTags"
              solo
              flat
              multiple
              label="Property Tags"
              :items="propertyTags"
              :dense="mobile"
              @change="fuzzySearch"
            >
              <template v-slot:prepend-item>
                <v-list-item ripple @click="selectAllFilterTags">
                  <v-list-item-action>
                    <v-icon
                      :color="filterTags.length > 0 ? 'indigo darken-4' : ''"
                      >{{ iconTags }}</v-icon
                    >
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>Select All</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
              </template>
              <template v-slot:selection="{ item, index }">
                <v-chip v-if="index === 0">
                  <span>{{ item.text }}</span>
                </v-chip>
                <span v-if="index === 1" class="grey--text caption"
                  >(+{{ filterTags.length - 1 }} others)</span
                >
              </template>
            </v-select>
          </v-col>
        </v-row>
      </div>
      <div v-if="!loading">
        <v-row
          v-if="propertyUnitList.units.length <= 0 && access.update"
          class="flex-grow-1"
        >
          <v-card
            class="
              flex-grow-1
              d-flex
              flex-column
              justify-center
              align-center
              card-radius
            "
            elevation="0"
            style="height: 60vh"
            @click="showOrganizationBuilder"
          >
            <p class="headline">You don't have any units yet!</p>
            <v-icon id="createUnits" x-large class="my-2"
              >mdi-plus-circle</v-icon
            >
            <p class="subtitle-1">
              Create your first set of units here using our template
            </p>
          </v-card>
        </v-row>
        <v-card
          v-if="propertyUnitList.units.length && access.read"
          elevation="0"
          class="card-radius"
        >
          <v-list dense>
            <draggable
              id="tree-menu-draggable"
              :disabled="!arrangeTreeMenu"
              :list="propertyUnitList.units"
              @end="changeIndexEnd"
            >
              <tree-menu-v2
                v-for="unit in propertyUnitList.units"
                :key="unit.id"
                :unit="unit"
                :depth="0"
                :activate-menu="activateMenu"
                :fuzzy-matched-address-ids="fuzzyMatchedAddressIds"
                :is-searching="isSearching"
                :reload-units="reloadUnits"
                :arrange-tree-menu="arrangeTreeMenu"
              ></tree-menu-v2>
            </draggable>
          </v-list>
        </v-card>
      </div>
      <v-skeleton-loader
        v-else
        ref="skeleton"
        type="table-tbody"
        loading
      ></v-skeleton-loader>
      <div class="py-2">
        <v-btn small text @click="toggleShowUnitId">Toggle unit ID</v-btn>
      </div>
    </v-container>
    <v-menu
      v-model="showBedMenu"
      :position-x="x"
      :position-y="y"
      absolute
      offset-y
      rounded="lg"
    >
      <v-list dense class="pa-0">
        <div v-if="bedStatus === 'occupied'">
          <v-list-item
            v-for="(item, index) in showFilteredMenu(bedMenuTop)"
            :key="index"
            :disabled="item.disabled"
            @click="item.action"
          >
            <v-list-item-icon class="mr-2">
              <v-icon v-if="!item.disabled" small>{{ item.icon }}</v-icon>
              <v-icon v-else class="grey--text" small>{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-title v-if="!item.disabled">{{
              item.title
            }}</v-list-item-title>
            <v-list-item-title v-else class="grey--text">{{
              item.title
            }}</v-list-item-title>
          </v-list-item>
        </div>
        <div v-else-if="bedStatus === 'vacant'">
          <v-list-item
            v-for="(item, index) in showFilteredMenu(bedMenuTopVacant)"
            :key="index"
            @click="item.action"
          >
            <v-list-item-icon class="mr-2">
              <v-icon small>{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </div>

        <div
          v-else-if="bedStatus === 'linked' || bedStatus === 'linked_vacant'"
        >
          <v-list-item
            v-for="(item, index) in showFilteredMenu(bedMenuTopLinked)"
            :key="index"
            @click="item.action"
          >
            <v-list-item-icon class="mr-2">
              <v-icon small>{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </div>

        <v-divider></v-divider>

        <div v-if="bedStatus === 'occupied'">
          <v-list-item
            v-for="(item, index) in showFilteredMenu(bedMenuBottom)"
            :key="'bottom' + index"
            @click="item.action"
          >
            <v-list-item-icon class="mr-2">
              <v-icon small>{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </div>
        <div v-else-if="bedStatus === 'vacant'">
          <v-list-item
            v-for="(item, index) in showFilteredMenu(bedMenuBottomVacant)"
            :key="'bottom' + index"
            @click="item.action"
          >
            <v-list-item-icon class="mr-2">
              <v-icon small>{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </div>
      </v-list>
    </v-menu>
    <v-menu
      v-model="showGroupMenu"
      :position-x="x"
      :position-y="y"
      absolute
      offset-y
      rounded="lg"
    >
      <v-list dense class="pa-0">
        <v-list-item
          v-for="(item, index) in showFilteredMenu(groupMenuTop)"
          :key="index"
          @click="item.action"
        >
          <v-list-item-icon class="mr-4">
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-title>{{ item.title }}</v-list-item-title>
        </v-list-item>
        <v-divider></v-divider>
        <v-list-item
          v-for="(item, index) in showFilteredMenu(groupMenuBottom)"
          :key="'bottom' + index"
          @click="item.action"
        >
          <v-list-item-icon class="mr-4">
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-title>{{ item.title }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
    <NewTenant ref="newTenant"></NewTenant>
    <NewBed ref="newBed"></NewBed>
    <NewGroup ref="newGroup"></NewGroup>
    <OrganizationBuilder ref="organizationBuilder"></OrganizationBuilder>
    <CollectUtilities ref="collectUtilities"></CollectUtilities>
    <CollectRentalPayment ref="collectRentalPayment"></CollectRentalPayment>
    <TransferTenant ref="transferTenant"></TransferTenant>
    <RenewLease ref="renewLease"></RenewLease>
    <UpdateBedName ref="updateBedName"></UpdateBedName>
    <UpdateGroupName ref="updateGroupName"></UpdateGroupName>
    <LinkUnit ref="linkUnit"></LinkUnit>
    <!-- <EditGroupTags ref="editGroupTags" @addTag="onAddTag"></EditGroupTags> -->
    <EditUnitTags ref="editUnitTags" @addTag="onAddTag"></EditUnitTags>
    <v-tour
      v-show="$store.state.units.propertyUnitListFlat.length"
      name="addTenantsTour"
      :steps="addTenantTourSteps"
      :callbacks="addTenantTourCallbacks"
      :options="addTenantTourOptions"
    ></v-tour>
    <v-snackbar v-model="snackbar">
      {{ snackbarMessage }}
      <v-btn text color="primary" @click.native="snackbar = false">Close</v-btn>
    </v-snackbar>
    <v-tour
      name="unitsTour"
      :steps="tourSteps"
      :callbacks="tourCallbacks"
    ></v-tour>
    <v-tour
      name="arrangeTour"
      :steps="arrangeTourSteps"
      :callbacks="arrangeTourCallbacks"
    ></v-tour>
  </div>
</template>

<script>
import NewTenant from '../components/units/NewTenant.vue'
import NewBed from '../components/units/NewBed.vue'
import NewGroup from '../components/units/NewGroup.vue'
import CollectUtilities from '../components/units/CollectUtilities.vue'
import CollectRentalPayment from '../components/payment/CollectRentalPayment.vue'
import RenewLease from '../components/units/RenewLease.vue'
import TransferTenant from '../components/units/TransferTenant.vue'
import UpdateBedName from '../components/units/UpdateBedName.vue'
import UpdateGroupName from '../components/units/UpdateGroupName.vue'
import TreeMenuV2 from '../components/units/TreeMenu/TreeMenuV2.vue'
import RightDrawer from '../components/RightDrawer.vue'
import LinkUnit from '../components/units/LinkUnit.vue'
// import EditGroupTags from '../components/units/EditGroupTags.vue'
import EditUnitTags from '../components/units/EditUnitTags.vue'
import draggable from 'vuedraggable'
import fuzzysort from 'fuzzysort'
import { bedModalPropAdapter } from '../util/unit-list'
import OrganizationBuilder from '../components/units/OrganizationBuilder/OrganizationBuilder.vue'

export default {
  components: {
    NewTenant,
    NewBed,
    NewGroup,
    OrganizationBuilder,
    CollectUtilities,
    CollectRentalPayment,
    TransferTenant,
    RenewLease,
    UpdateBedName,
    UpdateGroupName,
    TreeMenuV2,
    RightDrawer,
    LinkUnit,
    // EditGroupTags,
    EditUnitTags,
    draggable
  },
  data() {
    return {
      loading: true,
      showBedMenu: false,
      showGroupMenu: false,
      bedStatus: false,
      x: 0,
      y: 0,
      /**
       * @type {{
       *  created: string,
       *  updated: string,
       *  addressId: string,
       *  name: string,
       *  index: number,
       *  tags: {value: string, text: string}[]
       *  type: string,
       *  transientData?: {
       *   bedId?: string,
       *  }
       * }}
       */
      selectedUnit: {},
      selectedUnitSupplementaryData: {},
      tourSteps: [
        {
          target: '#title',
          content: 'Welcome to your the units page!',
          params: {
            placement: 'bottom-start'
          },
          offset: -500
        }
      ],
      tourCallbacks: {
        onStop: () => this.stopTourCallback('units')
      },
      snackbar: false,
      snackbarMessage: '',
      addTenantTourSteps: [
        {
          target: '.sp-unit',
          header: {
            title: 'Add Tenant'
          },
          content: 'Right Click here to add your first tenant!',
          params: {
            placement: 'bottom-start'
          },
          offset: -500
        }
      ],
      addTenantTourOptions: {
        startTimeout: 200
      },
      addTenantTourCallbacks: {
        onStop: () => this.stopTourCallback('addTenants')
      },
      searchString: '',
      /**
       * @type {'all' | 'occupied' | 'vacant'}
       */
      filterStatus: 'all',
      filterItems: [
        { value: 'all', text: 'All' },
        { value: 'occupied', text: 'Occupied' },
        { value: 'vacant', text: 'Vacant' }
      ],
      treeMenuKey: 0,
      movingParams: null,
      showUnitId: false,
      propertyTags: [],
      filterTags: [],
      arrangeTreeMenu: false,
      arrangeTourSteps: [
        {
          target: '#tree-menu-draggable',
          content: 'Drag units within the same group to rearrange them',
          params: {
            placement: 'right-start',
            enableScrolling: false
          }
        },
        {
          target: '#tree-menu-deactivate-arrange',
          content: 'Click here to exit the editor',
          params: {
            placement: 'right',
            enableScrolling: false
          }
        }
      ],
      arrangeTourCallbacks: {
        onStop: () => this.stopTourCallback('arrangeTour')
      },
      draggableEvent: {
        id: null,
        bedId: null,
        groupId: null,
        oldParentGroupId: null,
        newParentGroupId: null,
        oldIndex: null,
        newIndex: null
      },
      /**
       * List of bedIds that match the search string
       * undefined if no search string
       * @type {number[]}
       */
      fuzzyMatchedAddressIds: [],
      /**
       * Whether the user is currently searching
       */
      isSearching: false
    }
  },
  computed: {
    groupMenuTop() {
      return [
        {
          title: 'New Unit',
          icon: 'mdi-plus-box',
          action: this.newBed,
          show: this.access.create
        },
        {
          title: 'New Group',
          icon: 'mdi-plus-box',
          action: this.newGroupBySelected,
          show: this.access.create
        }
      ]
    },
    groupMenuBottom() {
      return [
        {
          title: 'Edit Group Name',
          icon: 'mdi-pencil',
          action: this.editGroupName,
          show: this.access.update
        },
        {
          title: 'Delete Group',
          icon: 'mdi-delete',
          action: this.deleteGroup,
          show: this.access.delete
        }
        // TODO: This not being used by anyone, so I will not implement this in the new schema
        // {
        //   title: 'Add/Remove Tags',
        //   icon: 'mdi-tag',
        //   action: this.editGroupTags,
        //   show: this.access.update
        // }
      ]
    },
    bedMenuTop() {
      return [
        {
          title: 'Collect Rental Payment',
          icon: 'mdi-bed',
          action: this.collectRentalPayment,
          show: this.paidpaymentAccess.create,
          disabled: this.paymentCompleted
        },
        {
          title: 'Create Payment Request',
          icon: 'mdi-cash-plus',
          action: this.collectUtilities,
          show: this.paymentrequestAccess.create
        },
        {
          title: 'Generate Statement of Account',
          icon: 'mdi-file-document',
          action: this.generateSOA,
          show: this.paymentAccess.statement
        }
      ]
    },
    bedMenuTopVacant() {
      return [
        {
          title: 'New Tenant',
          icon: 'mdi-account-plus',
          action: this.newTenant,
          show: this.leaseAccess.update
        },
        {
          title: 'Link Unit',
          icon: 'mdi-link',
          action: this.showLinkUnit,
          show: this.access.update
        }
      ]
    },
    bedMenuBottom() {
      return [
        {
          title: 'Edit Unit Name',
          icon: 'mdi-pencil',
          action: this.editBedName,
          show: this.access.update
        },
        {
          title: 'Update Lease Period',
          icon: 'mdi-autorenew',
          action: this.renewLease,
          show: this.leaseAccess.update
        },
        {
          title: 'Transfer Tenant',
          icon: 'mdi-account-arrow-right',
          action: this.transferTenant,
          show: this.leaseAccess.update
        },
        {
          title: 'Add/Remove Tags',
          icon: 'mdi-tag',
          action: this.editUnitTags,
          show: this.leaseAccess.update
        }
      ]
    },
    bedMenuBottomVacant() {
      return [
        {
          title: 'Edit Unit Name',
          icon: 'mdi-pencil',
          action: this.editBedName,
          show: this.access.update
        },
        {
          title: 'Delete Unit',
          icon: 'mdi-delete',
          action: this.deleteBed,
          show: this.access.delete
        },
        {
          title: 'Add/Remove Tags',
          icon: 'mdi-tag',
          action: this.editUnitTags,
          show: this.leaseAccess.update
        }
      ]
    },
    bedMenuTopLinked() {
      return [
        {
          title: 'Unlink Unit',
          icon: 'mdi-link-off',
          action: this.showUnlinkUnit,
          show: this.access.update
        }
      ]
    },
    user() {
      return this.$store.getters['auth/getUser']
    },
    propertyUnitList() {
      return this.$store.getters['units/getPropertyUnitList']
    },
    propertyUnitListFlat() {
      return this.$store.getters['units/getPropertyUnitListFlat']
    },
    allSupplementaryData() {
      return this.$store.getters['units/getPropertyUnitListSupplementaryData']
    },
    access() {
      return this.$root.$roleAccess('units').access
    },
    paymentAccess() {
      return this.$root.$roleAccess('payment').access
    },
    paymentrequestAccess() {
      return this.$root.$roleAccess('paymentrequest').access
    },
    paidpaymentAccess() {
      return this.$root.$roleAccess('paidpayment').access
    },
    leaseAccess() {
      return this.$root.$roleAccess('lease').access
    },
    property() {
      return this.$store.getters['properties/getProperty']
    },
    mobile() {
      return this.$vuetify.breakpoint.xs
    },
    nextRentalStartDate() {
      const leaseDetails = this.selectedUnitSupplementaryData.lease
      if (
        leaseDetails &&
        leaseDetails.nextRentalPeriod &&
        leaseDetails.nextRentalPeriod.startDate
      ) {
        return leaseDetails.nextRentalPeriod.startDate
      } else {
        return this.selectedUnitSupplementaryData.paidUntilDate
      }
    },
    paymentCompleted() {
      // TODO: cannot set to end of lease for now...
      // until we refactor the units to get /groups as heirarchy only
      // and get the data using `units/getUnitsFlat`
      return (
        this.nextRentalStartDate >
        this.selectedUnitSupplementaryData.lease.moveOutDate
      )
    },
    noTagsSelected() {
      return this.filterTags.length === 0
    },
    allTagsSelected() {
      return (
        this.filterTags.length ===
        (this.propertyTags && this.propertyTags.length)
      )
    },
    someTagsSelected() {
      return this.filterTags.length > 0 && !this.allCategoriesSelected
    },
    iconTags() {
      if (this.allTagsSelected) return 'mdi-close-box'
      if (this.someTagsSelected) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    menuItems() {
      return [
        {
          icon: 'mdi-plus',
          title: 'Create Group',
          show: this.access.create,
          action: () => this.newGroup()
        },
        {
          icon: 'mdi-layers-triple',
          title: 'Organize Units',
          show: this.access.edit,
          action: this.activateArrangeTreeMenu
        }
      ]
    },
    /**
     * @returns {string[]} Array of address IDs
     */
    matchedUnitByTags() {
      if (this.filterTags.length === 0) {
        return this.propertyUnitListFlat.map((unit) => unit.addressId)
      }
      return this.allSupplementaryData.filter((unit) => {
        const bedTags = unit.bed.tags || []
        return this.filterTags.some((tag) =>
          bedTags.some((bedTag) => bedTag.value === tag)
        )
      })
    },
    isSupplementaryDataLoading() {
      return this.$store.getters[
        'units/isPropertyUnitListSupplementaryDataLoading'
      ]
    }
  },
  created() {
    this.reloadUnits()
  },
  mounted() {
    const property = this.$store.getters['properties/getProperty']
    this.propertyTags = property.tags || []

    if (!this.user.tour || !this.user.tour['units']) {
      this.$tours['unitsTour'].start()
    }
  },
  updated() {
    if (!this.user.tour || !this.user.tour['units']) {
      this.$tours['unitsTour'].start()
    }
    if (
      this.$store.state.units.propertyUnitListFlat.length &&
      !this.user.tour['addTenants']
    ) {
      this.$tours['addTenantsTour'].start()
    }
  },
  methods: {
    getPropertyUnitListSupplementaryDataById(bedId) {
      const suppData =
        this.$store.getters['units/getPropertyUnitListSupplementaryDataMap']
      return suppData[bedId]
    },
    async reloadUnits() {
      this.loading = true
      await this.reloadUnitsDiscreetV2()
    },
    async reloadUnitsDiscreetV2() {
      await Promise.all([
        this.$store.dispatch('units/fetchPropertyUnitList').then(() => {
          this.loading = false
        }),
        this.$store.dispatch('units/fetchPropertyUnitListSupplementaryData')
      ])
    },
    showOrganizationBuilder: function () {
      //replace this
      //this.multipleDialog = !this.multipleDialog;
      this.$refs.organizationBuilder.showDialog()
    },
    preventDefault: function (event) {
      event.preventDefault()
    },
    activateMenu(e, unit) {
      e.preventDefault()
      if (this.isSupplementaryDataLoading) {
        return
      }
      this.selectedUnit = unit
      this.showBedMenu = false
      this.showGroupMenu = false
      this.x = e.clientX
      this.y = e.clientY
      this.$nextTick(() => {
        if (unit.type == 'unit') {
          this.selectedUnitSupplementaryData =
            this.getPropertyUnitListSupplementaryDataById(
              unit.transientData.bedId
            )
          this.bedStatus = this.getBedStatus(this.selectedUnitSupplementaryData)
          this.showBedMenu = true
        }
        if (unit.type == 'group') {
          this.showGroupMenu = true
        }
      })
    },
    newTenant() {
      if (!this.selectedUnit.transientData) {
        throw new Error('The selected unit does not have a valid bedId')
      }

      this.$refs.newTenant
        .open({
          bedId: this.selectedUnit.transientData.bedId,
          addressId: this.selectedUnit.addressId
        })
        .then((confirm) => {
          if (confirm) {
            this.reloadUnits()
          }
        })
    },
    newBed() {
      this.$refs.newBed
        .open({
          parentAddressId: this.selectedUnit.addressId
        })
        .then((confirm) => {
          if (confirm) {
            this.reloadUnits()
          }
        })
    },
    async deleteBed() {
      if (!this.selectedUnit.transientData) {
        throw new Error('The selected unit does not have a valid bedId')
      }
      if (
        await this.$root.$confirm({
          title: 'Delete Unit',
          message: `Are you sure you want to delete ${this.selectedUnit.name}?`
        })
      ) {
        this.loading = true
        this.$store
          .dispatch('units/deleteBedAction', {
            bedId: this.selectedUnit.transientData.bedId,
            addressId: this.selectedUnit.addressId
          })
          .then(() => {
            this.loading = false
            this.reloadUnits()
          })
      }
    },
    async deleteGroup() {
      if (
        await this.$root.$confirm({
          title: 'Delete Group',
          message: `Are you sure you want to delete ${this.selectedUnit.name}?`
        })
      ) {
        this.loading = true
        this.$store
          .dispatch('units/deleteGroupAction', {
            addressId: this.selectedUnit.addressId
          })
          .then(() => {
            this.reloadUnits()
          })
          .catch((err) => {
            this.loading = false
            this.snackbarMessage = err
            this.snackbar = true
          })
      }
    },
    collectUtilities() {
      this.$refs.collectUtilities.open(
        bedModalPropAdapter(this.selectedUnitSupplementaryData)
      )
    },
    async collectRentalPayment() {
      this.$refs.collectRentalPayment.open(
        bedModalPropAdapter(this.selectedUnitSupplementaryData)
      )
    },
    transferTenant() {
      this.$refs.transferTenant
        .open(bedModalPropAdapter(this.selectedUnitSupplementaryData))
        .then(() => {
          this.reloadUnits()
        })
    },
    renewLease() {
      this.$refs.renewLease.open(
        bedModalPropAdapter(this.selectedUnitSupplementaryData)
      )
    },
    async generateSOA() {
      const tenantId = this.selectedUnitSupplementaryData.tenant.userId
      this.$root.$setLoading(true)
      const asyncJob = await this.$store.dispatch(
        'asyncJobs/createSOAAsyncJob',
        { tenantId }
      )
      this.$root.$setLoading(false)
      if (!asyncJob || !asyncJob.jobId) {
        this.$root.$confirm({
          title: 'Error',
          message: 'Failed to generate Statement of Account. Please try again.',
          options: {
            color: 'error'
          }
        })
        return
      }
      this.$router.push(`/asyncJobs/${asyncJob.jobId}`)
    },
    async stopTourCallback(section) {
      await this.$store.dispatch('auth/stopTour', { section })
    },
    editBedName() {
      this.$refs.updateBedName
        .open({
          bedId: this.selectedUnit.transientData.bedId,
          oldBedName: this.selectedUnit.name,
          addressId: this.selectedUnit.addressId
        })
        .then((confirm) => {
          if (confirm) {
            this.reloadUnits()
          }
        })
    },
    editGroupName() {
      this.$refs.updateGroupName
        .open({
          oldName: this.selectedUnit.name,
          addressId: this.selectedUnit.addressId
        })
        .then((confirm) => {
          if (confirm) {
            this.reloadUnits()
          }
        })
    },
    newGroup() {
      this.$refs.newGroup.open({ parentAddressId: null }).then((confirm) => {
        if (confirm) {
          this.reloadUnits()
        }
      })
    },
    newGroupBySelected() {
      this.$refs.newGroup
        .open({ parentAddressId: this.selectedUnit.addressId })
        .then((confirm) => {
          if (confirm) {
            this.reloadUnits()
          }
        })
    },
    // editGroupTags() {
    //   this.$refs.editGroupTags.open({
    //     groupId: this.selectedUnit.groupId,
    //     groupName: this.selectedUnit.groupName
    //   })
    // },
    editUnitTags() {
      this.$refs.editUnitTags.open({
        bedId: this.selectedUnit.transientData.bedId,
        bedName: this.selectedUnit.bedName
      })
    },
    showFilteredMenu(menu) {
      return menu.filter((item) => item.show != 0)
    },
    showLinkUnit() {
      this.$refs.linkUnit
        .open({
          bedId: this.selectedUnit.transientData.bedId
        })
        .then((result) => {
          if (result) {
            this.reloadUnits()
          }
        })
    },
    async showUnlinkUnit() {
      const parentUnitSupplementaryData =
        this.getPropertyUnitListSupplementaryDataById(
          this.selectedUnitSupplementaryData.linkedParentBedId
        )
      if (
        await this.$root.$confirm({
          title: 'Unlink Unit',
          message: `Are you sure you want to unlink ${this.selectedUnit.name} from ${parentUnitSupplementaryData.bed.name}?`
        })
      ) {
        await this.$store
          .dispatch('units/unlinkUnitAction', {
            bedId: this.selectedUnitSupplementaryData.bed.bedId
          })
          .then(() => {
            this.reloadUnits()
          })
      }
    },
    async changeIndexEnd(event) {
      this.$root.$setLoading(true)
      const itemAddressId = event.item.id.split('tree_menu_')[1]

      const draggableEvent = {
        newIndex: event.newIndex,
        addressId: itemAddressId,
        newParentAddressId: null
      }

      // push the event to store
      try {
        await this.$store.dispatch('units/moveUnit', draggableEvent)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
      this.$root.$setLoading(false)
    },
    getUnitUser(unit) {
      const leaseDetails = unit && unit.lease_details
      const alias = this.selectedUnitSupplementaryData.alias
      const aliasName = alias && alias.name
      if (aliasName) {
        return aliasName
      }

      let name = '(empty)'
      if (unit.status === 'linked' || unit.status === 'linked_vacant') {
        name = `Linked to ${unit.parentUnit_details.bedName}`
      } else if (leaseDetails) {
        const tenant_details = leaseDetails && leaseDetails.tenant_details
        if (tenant_details) {
          name = ''
          if (tenant_details.firstName) {
            name += tenant_details.firstName + ' '
          }
          if (tenant_details.lastName) {
            name += tenant_details.lastName + ' '
          }
          if (tenant_details.username) {
            name += `(${tenant_details.username})`
          }
        }
      }
      return name
    },
    toggleShowUnitId() {
      this.$store.dispatch('units/toggleShowUnitIdAction')
    },
    /**
     * Uses fuzzysort to search for keywords from supplementary data
     * And returns a list of addressIds that match the search string
     * AddressIs are needed to show the ancestors of the search results
     */
    fuzzySearch() {
      this.isSearching =
        this.searchString.length > 0 ||
        this.filterTags.length > 0 ||
        this.filterStatus !== 'all'

      /**
       * @type {{bedId: number, addressId: string}[]}
       */
      let bedsToShow = this.propertyUnitListFlat.map((unit) => {
        return {
          bedId: unit.transientData && unit.transientData.bedId,
          addressId: unit.addressId
        }
      })

      let unitListSearchResults = []
      // Filter by search string
      if (this.searchString.length > 0) {
        const occupiedSearchResults = fuzzysort.go(
          this.searchString,
          this.allSupplementaryData,
          {
            key: 'combinedTags'
          }
        )

        unitListSearchResults = fuzzysort
          .go(this.searchString, this.propertyUnitListFlat, {
            key: 'name'
          })
          .map((result) => result.obj.addressId)

        /**
         * @type {string[]}
         */
        const bedIdsToShow = occupiedSearchResults
          .filter((result) => result.score > -10000)
          .map((result) => result.obj.bed.bedId)

        bedsToShow = bedsToShow.filter((unit) =>
          bedIdsToShow.includes(unit.bedId)
        )
      }

      // Filter by tags
      if (this.filterTags.length > 0) {
        const matchedUnitByTagsAddressIds = this.matchedUnitByTags.map(
          (m) => m.bed && m.bed.bedId
        )
        bedsToShow = bedsToShow.filter((unit) =>
          matchedUnitByTagsAddressIds.includes(unit.bedId)
        )
      }

      // Filter by status
      if (this.filterStatus === 'vacant') {
        bedsToShow = bedsToShow.filter((bed) => {
          const supplementaryData =
            this.getPropertyUnitListSupplementaryDataById(bed.bedId)
          return !supplementaryData
        })
      } else if (this.filterStatus === 'occupied') {
        bedsToShow = bedsToShow.filter((bed) => {
          const supplementaryData =
            this.getPropertyUnitListSupplementaryDataById(bed.bedId)
          return !!supplementaryData
        })
      }

      // Intersect
      this.fuzzyMatchedAddressIds = [
        ...bedsToShow.map((unit) => unit.addressId),
        ...unitListSearchResults
      ]
    },
    concat(...arrays) {
      return [].concat(...arrays.filter(Array.isArray))
    },
    selectAllFilterTags() {
      this.$nextTick(() => {
        if (this.allTagsSelected) {
          this.filterTags = []
        } else {
          this.filterTags = this.propertyTags.map((t) => t.value)
        }
        this.fuzzySearch()
      })
    },
    onAddTag(tag) {
      // check uniqueness
      // update property tags
      const snakeCaseTag = tag.value
      if (
        this.propertyTags.findIndex(
          (propertyTag) => propertyTag.value === snakeCaseTag
        ) !== -1
      ) {
        return
      }

      this.propertyTags.push(tag)

      // run get units in the background
      this.reloadUnitsDiscreetV2()
    },
    activateArrangeTreeMenu() {
      if (!this.user.tour['arrangeTour']) {
        this.$tours['arrangeTour'].start()
      }
      this.arrangeTreeMenu = true
    },
    async deactivateArrangeTreeMenu() {
      this.arrangeTreeMenu = false
      this.reloadUnitsDiscreetV2()
    },
    getBedStatus(supplementaryData) {
      if (supplementaryData) {
        return supplementaryData.bed.status
      }
      return 'vacant'
    }
  }
}
</script>

<style scoped>
.container {
  width: auto;
}
.container.main-container {
  min-height: 100vh;
}
.v-card.card-radius {
  border-radius: 12px !important;
}
.v-menu__content {
  box-shadow: 0px 5px 5px -3px rgba(0, 0, 0, 0.05),
    0px 8px 10px 1px rgba(0, 0, 0, 0.05), 0px 3px 14px 2px rgba(0, 0, 0, 0.1) !important;
}
.v-input {
  border-radius: 10px;
}
</style>
